import { useState, useMemo } from 'react';
import HistoryIcon from '@mui/icons-material/History';
import {
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
} from '@mui/material';
import { PageHeader, FilterTabs, StatusChip, EmptyState, LoadingState } from '@/components/shared';
import { SeoChangelog } from '@/types/api/seo';

type Props = {
  entries: SeoChangelog[];
  isLoading: boolean;
  onRevert: (id: string) => Promise<void>;
  isReverting: boolean;
};

type FilterKey = 'all' | 'applied' | 'shadow' | 'reverted';

const FIELD_STATUS_MAP: Record<string, { label: string; color: string }> = {
  title: { label: 'Title', color: '#054AD9' },
  meta_description: { label: 'Meta Description', color: '#7B61FF' },
  h1: { label: 'H1', color: '#0891B2' },
  h2: { label: 'H2', color: '#0891B2' },
  body: { label: 'Body', color: '#059669' },
  slug: { label: 'Slug', color: '#D97706' },
  schema: { label: 'Schema', color: '#DC2626' },
  internal_links: { label: 'Internal Links', color: '#7C3AED' },
  canonical: { label: 'Canonical', color: '#6366F1' },
};

const SOURCE_COLORS: Record<string, string> = {
  agent: '#054AD9',
  manual: '#71717A',
  copilot: '#7B61FF',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

function stringifyValue(val: any): string {
  if (val === null || val === undefined) return '(empty)';
  if (typeof val === 'object') return JSON.stringify(val);
  return String(val);
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max)}...`;
}

function truncateUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname + u.search;
    return truncate(path, 40);
  } catch {
    return truncate(url, 40);
  }
}

function qualityColor(score: number): string {
  if (score >= 8) return '#059669';
  if (score >= 5) return '#D97706';
  return '#DC2626';
}

function DiffCell({ oldValue, newValue }: { oldValue: any; newValue: any }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const oldStr = stringifyValue(oldValue);
  const newStr = stringifyValue(newValue);
  const needsTruncation = oldStr.length > 80 || newStr.length > 80;

  const displayOld = expanded ? oldStr : truncate(oldStr, 80);
  const displayNew = expanded ? newStr : truncate(newStr, 80);

  return (
    <Box
      sx={{ cursor: needsTruncation ? 'pointer' : 'default', maxWidth: 320 }}
      onClick={needsTruncation ? () => setExpanded(!expanded) : undefined}
    >
      <Typography
        variant="body2"
        sx={{
          color: '#DC2626',
          textDecoration: 'line-through',
          fontSize: 13,
          lineHeight: 1.4,
          wordBreak: 'break-word',
        }}
      >
        {displayOld}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: '#059669',
          fontSize: 13,
          lineHeight: 1.4,
          wordBreak: 'break-word',
        }}
      >
        {displayNew}
      </Typography>
      {needsTruncation && !expanded && (
        <Typography sx={{ fontSize: 11, color: '#71717A', mt: '2px' }}>Click to expand</Typography>
      )}
    </Box>
  );
}

const SeoChangelogComponent = function ({
  entries,
  isLoading,
  onRevert,
  isReverting,
}: Props): JSX.Element {
  const [filter, setFilter] = useState<FilterKey>('all');

  const counts = useMemo(() => {
    let applied = 0;
    let shadow = 0;
    let reverted = 0;
    for (const e of entries) {
      if (e.revertedAt) reverted += 1;
      else if (e.shadow) shadow += 1;
      else applied += 1;
    }
    return { all: entries.length, applied, shadow, reverted };
  }, [entries]);

  const filtered = useMemo(() => {
    if (filter === 'all') return entries;
    if (filter === 'applied') return entries.filter((e) => !e.shadow && !e.revertedAt);
    if (filter === 'shadow') return entries.filter((e) => e.shadow);
    if (filter === 'reverted') return entries.filter((e) => !!e.revertedAt);
    return entries;
  }, [entries, filter]);

  if (isLoading) {
    return (
      <>
        <PageHeader title="Changelog" />
        <LoadingState message="Loading changelog..." />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Changelog" />

      <FilterTabs
        tabs={[
          { key: 'all', label: 'All', count: counts.all },
          { key: 'applied', label: 'Applied', count: counts.applied },
          { key: 'shadow', label: 'Shadow', count: counts.shadow },
          { key: 'reverted', label: 'Reverted', count: counts.reverted },
        ]}
        activeKey={filter}
        onChange={(key) => setFilter(key as FilterKey)}
      />

      {filtered.length === 0 ? (
        <EmptyState
          icon={HistoryIcon}
          title="No changelog entries"
          description="Changes made by agents, copilot, or manually will appear here."
        />
      ) : (
        <TableContainer
          sx={{
            border: '2px solid #F4F4F5',
            borderRadius: '12px',
            mt: 2,
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: '#F4F4F5' }}>
                <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>Page</TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>Change</TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: 12 }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: 12, textAlign: 'center' }}>
                  Quality
                </TableCell>
                <TableCell sx={{ fontWeight: 500, fontSize: 12, textAlign: 'right' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((entry) => {
                const isReverted = !!entry.revertedAt;
                const fieldKey = entry.fieldName.toLowerCase().replace(/\s+/g, '_');
                const fieldConfig = FIELD_STATUS_MAP[fieldKey] ?? {
                  label: entry.fieldName,
                  color: '#71717A',
                };

                return (
                  <TableRow
                    key={entry.id}
                    sx={{
                      opacity: isReverted ? 0.55 : 1,
                      '& td': { borderBottom: '1px solid hsla(0,0%,0%,0.08)' },
                    }}
                  >
                    <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                      <span style={{ textDecoration: isReverted ? 'line-through' : 'none' }}>
                        {formatDate(entry.appliedAt)}
                      </span>
                    </TableCell>

                    <TableCell sx={{ fontSize: 13, maxWidth: 180 }}>
                      {entry.pageUrl ? (
                        <Tooltip title={entry.pageUrl} arrow>
                          <a
                            href={entry.pageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#054AD9',
                              textDecoration: isReverted ? 'line-through' : 'none',
                              fontSize: 13,
                            }}
                          >
                            {truncateUrl(entry.pageUrl)}
                          </a>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: 13, color: '#71717A' }}>--</Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <StatusChip
                        status={fieldKey}
                        statusMap={{ [fieldKey]: fieldConfig }}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <DiffCell oldValue={entry.oldValue} newValue={entry.newValue} />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={entry.appliedBy}
                        size="small"
                        sx={{
                          fontSize: 11,
                          fontWeight: 500,
                          height: 22,
                          bgcolor: SOURCE_COLORS[entry.appliedBy] ?? '#71717A',
                          color: '#fff',
                          borderRadius: '4px',
                        }}
                      />
                    </TableCell>

                    <TableCell sx={{ textAlign: 'center' }}>
                      {entry.llmQualityScore != null ? (
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: qualityColor(entry.llmQualityScore),
                          }}
                        >
                          {entry.llmQualityScore}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 13, color: '#71717A' }}>--</Typography>
                      )}
                    </TableCell>

                    <TableCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isReverted ? (
                        <Chip
                          label="Reverted"
                          size="small"
                          sx={{
                            fontSize: 11,
                            fontWeight: 500,
                            height: 22,
                            bgcolor: '#FEE2E2',
                            color: '#DC2626',
                            borderRadius: '4px',
                          }}
                        />
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={entry.shadow || isReverting}
                          onClick={() => onRevert(entry.id)}
                          sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            textTransform: 'none',
                            borderRadius: '999px',
                            minHeight: 28,
                            px: 1.5,
                            borderColor: '#F4F4F5',
                            color: '#4A4B4E',
                            '&:hover': { borderColor: '#054AD9', color: '#054AD9' },
                            '&:active': { borderColor: '#054AD9', color: '#054AD9' },
                          }}
                        >
                          Revert
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

export default SeoChangelogComponent;
